import { useEffect, useMemo } from 'react';
import type { PageUI } from '@alltrails/layout/utils/PageUI/pageUI';
import { useAppDispatch } from '../redux';
import { updateTrailResults } from '../redux/reducer';
import convertResults from '../utils/convertResults';
import fitBoundsOnLoad from '../utils/fitBoundsOnLoad';
import { sectionToTrailResults } from '../utils/pageUITrailResults';
import useFitBoundsOnce from './useFitBoundsOnce';

// The map is framed once per response, so each response gets a stable key to remember it by. RTK Query hands every reader
// of a response the same object, so its identity tells a new response apart from a re-render, the other slot reading the
// same response, or a remount.
const responseKeys = new WeakMap<PageUI, string>();
let responseCount = 0;

const framingDuration = 1000;

const getResponseKey = (page: PageUI) => {
  let key = responseKeys.get(page);

  if (!key) {
    responseCount += 1;
    key = `response-${responseCount}`;
    responseKeys.set(page, key);
  }

  return key;
};

/**
 * Puts a PageUI response on the map: pins in the order the server sent them, framed once per response.
 * Pass `undefined` while the response is loading; the map is left as it is until there is one.
 */
const usePageUITrailResults = (page: PageUI | undefined) => {
  const dispatch = useAppDispatch();
  const results = useMemo(() => page?.sections.flatMap(sectionToTrailResults) ?? [], [page]);

  useEffect(() => {
    if (!page) {
      return;
    }

    dispatch(updateTrailResults(results));
  }, [dispatch, page, results]);

  // Frames to the trailheads only, so a hovered trail's polyline appearing later never moves the map. With no results there
  // is nothing to frame to, so the map stays where it is.
  useFitBoundsOnce('pageUITrailResults', page && results.length ? getResponseKey(page) : undefined, map => {
    fitBoundsOnLoad(convertResults(results), map, framingDuration, map.getPadding(), 12, false /* linear */);
  });
};

export default usePageUITrailResults;
